/**
 * Contact Repository
 * 문의 데이터 액세스 계층
 */

import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import {
  Contact,
  ContactAssignRequest,
  ContactResponseRequest,
  ContactRejectRequest,
  ContactStatusUpdateRequest,
  ContactStatus,
  ServiceType,
} from '../models';

export class ContactRepository {
  // 문의 API 경로 (백엔드 api/contacts.py admin_router와 일치)
  private baseUrl = `${API_BASE_URL}/api/v1/admin/contacts`;

  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async getAllContacts(serviceType?: ServiceType): Promise<Contact[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(this.baseUrl, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getPendingContacts(serviceType?: ServiceType): Promise<Contact[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(`${this.baseUrl}/pending`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getInProgressContacts(serviceType?: ServiceType): Promise<Contact[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(`${this.baseUrl}/in-progress`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getMyContacts(): Promise<Contact[]> {
    const response = await axios.get(`${this.baseUrl}/my-contacts`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getContactById(id: string): Promise<Contact> {
    const response = await axios.get(`${this.baseUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async assignContact(request: ContactAssignRequest): Promise<Contact> {
    const response = await axios.post(
      `${this.baseUrl}/${request.contact_id}/assign`,
      { admin_id: request.admin_id },
      { headers: this.getAuthHeaders() }
    );
    return response.data.contact;
  }

  async respondToContact(request: ContactResponseRequest): Promise<Contact> {
    const response = await axios.post(
      `${this.baseUrl}/${request.contact_id}/respond`,
      { response: request.response },
      { headers: this.getAuthHeaders() }
    );
    return response.data.contact;
  }

  async rejectContact(request: ContactRejectRequest): Promise<Contact> {
    const response = await axios.post(
      `${this.baseUrl}/${request.contact_id}/reject`,
      { rejection_reason: request.rejection_reason },
      { headers: this.getAuthHeaders() }
    );
    return response.data.contact;
  }

  async updateContactStatus(request: ContactStatusUpdateRequest): Promise<Contact> {
    const response = await axios.put(
      `${this.baseUrl}/${request.contact_id}/status`,
      { status: request.status },
      { headers: this.getAuthHeaders() }
    );
    return response.data.contact;
  }

  async getPendingCount(): Promise<number> {
    const response = await axios.get(`${this.baseUrl}/stats/pending-count`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.pending_count;
  }
}
